import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Zap, Flame, Check, FileText, ExternalLink } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://static.wixstatic.com/media/709795_dcb4777a39394c9fb86ba6ad9678de09~mv2.png";

// Prezzi fissi come da centrale di controllo
const PREZZI = {
  luce_spread: "0.013000",
  luce_comm: "102,00",
  gas_spread: "0.110000",
  gas_comm: "102,00",
  data_scadenza: "30 Aprile 2026"
};

// Link documenti
const DOCS = {
  cte_luce: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  cte_gas: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  condizioni: "https://customer-assets.emergentagent.com/job_f56f21f7-c17f-4649-a9ca-6cb78c28c69d/artifacts/4adzcav1_Condizioni-Generali2722026.pdf"
};

export default function CompareOffers() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="compare-offers-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="8energy" className="h-8" />
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-gray-500 hover:text-gray-900" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        {/* Promo Banner */}
        <motion.div
          className="flex justify-center mb-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div 
            className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full border-2 border-[#ADFF2F] font-extrabold text-sm"
            style={{ animation: 'heartbeat 2s infinite' }}
          >
            <Zap className="w-4 h-4 text-[#006838]" strokeWidth={2} />
            <span>PROMO PLUS ONLINE</span>
            <span className="text-gray-500">•</span>
            <span>Fino al {PREZZI.data_scadenza}</span>
          </div>
        </motion.div>

        {/* Legal Notice */}
        <p className="text-center text-xs text-gray-400 uppercase tracking-wider font-bold mb-10">
          Offerta esclusiva Web • Validazione tramite firma digitale online
        </p>

        {/* Offers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Luce Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <OfferCard
              type="luce"
              title="Luce Plus Online"
              subtitle="Luce Domestico"
              formula="PUN"
              spread={PREZZI.luce_spread}
              unit="€/kWh"
              commission={PREZZI.luce_comm}
              features={["Energia 100% Green"]}
              cteLink={DOCS.cte_luce}
              conditionsLink={DOCS.condizioni}
              color="#006838"
            />
          </motion.div>

          {/* Gas Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <OfferCard
              type="gas"
              title="Gas Plus Online"
              subtitle="Gas Domestico"
              formula="PSV"
              spread={PREZZI.gas_spread}
              unit="€/Smc"
              commission={PREZZI.gas_comm}
              features={["Prezzo Trasparente"]}
              cteLink={DOCS.cte_gas}
              conditionsLink={DOCS.condizioni}
              color="#FF6B00"
            />
          </motion.div>
        </div>

        {/* Final CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-gray-100 shadow-xl shadow-black/5 relative overflow-hidden">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Attiva la tua offerta <span className="text-[#006838]">Online.</span>
                </h2>
                <p className="text-gray-500 mb-8">
                  Sottoscrivi il contratto in pochi minuti. La validità dell'offerta è garantita solo tramite procedura digitale.
                </p>
                
                <Link to="/switch-guide">
                  <button 
                    className="inline-flex items-center gap-3 bg-[#006838] text-white px-8 py-5 rounded-full font-extrabold text-lg shadow-xl shadow-[#006838]/25 hover:bg-[#ADFF2F] hover:text-[#006838] hover:-translate-y-1 transition-all duration-300"
                    data-testid="activate-offer-btn"
                  >
                    <i className="fas fa-signature"></i>
                    FIRMA E ATTIVA ORA
                  </button>
                </Link>

                <p className="text-xs text-gray-400 mt-6">
                  *I prezzi si riferiscono alla componente energia e materia prima. IVA e imposte escluse.
                </p>
              </div>

              {/* OTTO Character */}
              <motion.img
                src={OTTO_URL}
                alt="8Energy Assistant"
                className="w-40 md:w-52 hidden md:block"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </div>
        </motion.div>
      </main>

      {/* Styles */}
      <style>{`
        @keyframes heartbeat {
          0% { transform: scale(1); box-shadow: 0 0 10px rgba(173, 255, 47, 0.2); }
          50% { transform: scale(1.03); box-shadow: 0 0 25px rgba(173, 255, 47, 0.5); }
          100% { transform: scale(1); box-shadow: 0 0 10px rgba(173, 255, 47, 0.2); }
        }
        @keyframes rotateBorder {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function OfferCard({ type, title, subtitle, formula, spread, unit, commission, features, cteLink, conditionsLink, color }) {
  const isLuce = type === "luce";
  
  return (
    <div 
      className="relative rounded-[2.5rem] p-[3px] overflow-hidden hover:-translate-y-2 transition-transform duration-300"
      data-testid={`offer-card-${type}`}
    >
      {/* Rotating Border Effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'conic-gradient(transparent, #ADFF2F, #006838, transparent 30%)',
          animation: 'rotateBorder 4s linear infinite'
        }}
      />
      <div 
        className="absolute inset-0"
        style={{
          background: 'conic-gradient(transparent, #ADFF2F, #006838, transparent 30%)',
          animation: 'rotateBorder 4s linear infinite',
          transform: 'rotate(180deg)'
        }}
      />
      
      {/* Card Content */}
      <div className="relative bg-white rounded-[2.2rem] p-8 z-10 h-full flex flex-col">
        {/* Subtitle */}
        <span 
          className="font-black text-xs uppercase tracking-widest"
          style={{ color }}
        >
          {subtitle}
        </span>
        
        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mt-2 mb-6">{title}</h2>
        
        {/* Price Box */}
        <div className="bg-[#f5f5f7] rounded-2xl p-6 text-center mb-6">
          <span className="text-gray-600">{formula} + </span>
          <span className="text-2xl font-bold text-[#006838]">{spread}</span>
          <span className="text-xl font-bold text-[#006838]"> {unit}*</span>
        </div>
        
        {/* Features */}
        <ul className="space-y-3 mb-8 flex-1">
          <li className="flex items-center gap-3 text-sm text-gray-600">
            <Check className="w-4 h-4 flex-shrink-0" style={{ color }} strokeWidth={2} />
            Comm.ne: €{commission}/anno
          </li>
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3 text-sm text-gray-600">
              <Check className="w-4 h-4 flex-shrink-0" style={{ color }} strokeWidth={2} />
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Document Links */}
        <div className="flex gap-3">
          <a
            href={cteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-100 text-xs font-bold text-gray-700 hover:text-[#006838] hover:border-[#ADFF2F] hover:bg-[#fafff0] transition-all"
          >
            <FileText className="w-3 h-3" strokeWidth={2} />
            CTE {isLuce ? "LUCE" : "GAS"}
          </a>
          <a
            href={conditionsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-100 text-xs font-bold text-gray-700 hover:text-[#006838] hover:border-[#ADFF2F] hover:bg-[#fafff0] transition-all"
          >
            <FileText className="w-3 h-3" strokeWidth={2} />
            CONDIZIONI
          </a>
        </div>
      </div>
    </div>
  );
}
